import { useEffect, useState } from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { NotificationTopicOwnersSettingsSection } from "@/components/NotificationTopicOwnersSettingsSection";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { HubSettingsFooter, HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import { Input, Label } from "@/components/ui/Input";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { formatTimestamp } from "@/lib/utils";

import {
  CLAMPQC_HUB_SETTINGS_QUERY_KEY,
  CLAMPQC_PHYSICAL_FAILURE_REASONS_QUERY_KEY,
  fetchClampQcHubSettings,
  patchClampQcHubSettings,
  type ClampQcHubSettingsPatchRequest,
  type ClampQcHubSettingsResponse,
} from "./clampQcSettingsApi";
import { ClampQcPhysicalFailReasonsSettingsSection } from "./ClampQcPhysicalFailReasonsSettingsSection";
import { ApiError } from "@/lib/api";
import { fetchHubMockupEffective } from "@/lib/hubMockupApi";

const NOTIFICATION_STAFF_USERS_QUERY_KEY = ["notifications", "staff-user-options"] as const;

function fetchNotificationStaffUsers(): Promise<{
  users: Array<{ userId: string; email: string; displayName: string | null }>;
}> {
  return apiFetch("/api/notifications/staff-user-options");
}

const CLAMP_QC_SETTINGS_PAGE_HELP_TEXT =
  "Large clamp SKUs use prefix 90-003; small clamp line uses 90-002. Each card can be saved on its own, or use Save all settings at the bottom to validate and write every field in one request. All paths update the single JSON document at workflow.app_settings key hub.clampqc (the same document you can edit from Global app settings), including carton counts, inductance sampling percent, inductance bands, and separate unloaded-line bands and drag windows for each prefix. Mechanical fail reasons are managed separately in quality.failure_reason.";

const CLAMP_QC_CARTONS_SECTION_HELP_TEXT =
  "These numbers are the carton size for each clamp line and drive both trackers on the job workspace. Pre-QC starts at this count and ticks down on every inspection. Post-QC starts at 0 and ticks up on a pass; when it reaches this size the hub prints a 4x2 inventory label (90-002-TLA or 90-003-TLA) with the work order. Large line uses SKU prefix 90-003; small line uses 90-002.";

const CLAMP_QC_INDUCTANCE_RATE_SECTION_HELP_TEXT =
  "Each new serial on a QC work order receives the next QC ordinal from 1 through planned quantity. The hub picks a fixed-size random subset of ordinals for inductance: half from the first 10% of the order, one quarter from the next 25%, and one quarter from the remaining 65%, with shortfalls spilling forward. Within each band the picks are mixed and spaced so a low sample rate does not ask for inductance on several units in a row. The schedule is deterministic for a given build order ID so all stations agree. Once a hub.clampqc JSON row exists in the database, inductanceTestingPercentPerWorkOrder must be present there or the API returns 422 (no silent default). Use Save here to set the rate explicitly. Changing the percent only affects new reservations; rows already stored in workflow.clamp_qc_inspection_slot keep their original requires-inductance flag, so a work order inspected under 100% can keep asking for inductance on every serial until those rows are cleared or you use a fresh build order.";

type SectionSaveKind = "cartons" | "inductanceRate" | "inductanceBand" | "lineCurrent" | "drag" | "all";

function buildCartonsPatch(
  largeDraft: string,
  smallDraft: string,
): Pick<ClampQcHubSettingsPatchRequest, "unitsPerCartonLarge" | "unitsPerCartonSmall"> {
  const unitsLarge = Number.parseInt(largeDraft.trim(), 10);
  const unitsSmall = Number.parseInt(smallDraft.trim(), 10);
  if (!Number.isFinite(unitsLarge) || !Number.isFinite(unitsSmall)) {
    throw new Error("Large and Small carton counts must be whole numbers.");
  }
  return { unitsPerCartonLarge: unitsLarge, unitsPerCartonSmall: unitsSmall };
}

function buildInductanceRatePatch(
  inductanceTestingPercentDraft: string,
): Pick<ClampQcHubSettingsPatchRequest, "inductanceTestingPercentPerWorkOrder"> {
  const inductanceTestingPercentPerWorkOrder = Number.parseFloat(inductanceTestingPercentDraft.trim());
  if (!Number.isFinite(inductanceTestingPercentPerWorkOrder)) {
    throw new Error("Inductance testing percent must be a number.");
  }
  if (inductanceTestingPercentPerWorkOrder < 0 || inductanceTestingPercentPerWorkOrder > 100) {
    throw new Error("Inductance testing percent must be between 0 and 100.");
  }
  return { inductanceTestingPercentPerWorkOrder };
}

function buildInductanceBandPatch(
  indMinLargeDraft: string,
  indMaxLargeDraft: string,
  indMinSmallDraft: string,
  indMaxSmallDraft: string,
): Pick<
  ClampQcHubSettingsPatchRequest,
  "inductanceMinLarge" | "inductanceMaxLarge" | "inductanceMinSmall" | "inductanceMaxSmall"
> {
  const inductanceMinLarge = Number.parseFloat(indMinLargeDraft.trim());
  const inductanceMaxLarge = Number.parseFloat(indMaxLargeDraft.trim());
  const inductanceMinSmall = Number.parseFloat(indMinSmallDraft.trim());
  const inductanceMaxSmall = Number.parseFloat(indMaxSmallDraft.trim());
  if (
    !Number.isFinite(inductanceMinLarge) ||
    !Number.isFinite(inductanceMaxLarge) ||
    !Number.isFinite(inductanceMinSmall) ||
    !Number.isFinite(inductanceMaxSmall)
  ) {
    throw new Error("Inductance limits must be valid numbers.");
  }
  if (inductanceMinLarge > inductanceMaxLarge || inductanceMinSmall > inductanceMaxSmall) {
    throw new Error("Each line’s minimum inductance must be less than or equal to its maximum.");
  }
  return { inductanceMinLarge, inductanceMaxLarge, inductanceMinSmall, inductanceMaxSmall };
}

function buildLineCurrentPatch(
  unloadedMinLargeDraft: string,
  unloadedMaxLargeDraft: string,
  unloadedMinSmallDraft: string,
  unloadedMaxSmallDraft: string,
): Pick<
  ClampQcHubSettingsPatchRequest,
  "unloadedMinLarge" | "unloadedMaxLarge" | "unloadedMinSmall" | "unloadedMaxSmall"
> {
  const unloadedMinLarge = Number.parseFloat(unloadedMinLargeDraft.trim());
  const unloadedMaxLarge = Number.parseFloat(unloadedMaxLargeDraft.trim());
  const unloadedMinSmall = Number.parseFloat(unloadedMinSmallDraft.trim());
  const unloadedMaxSmall = Number.parseFloat(unloadedMaxSmallDraft.trim());
  if (
    !Number.isFinite(unloadedMinLarge) ||
    !Number.isFinite(unloadedMaxLarge) ||
    !Number.isFinite(unloadedMinSmall) ||
    !Number.isFinite(unloadedMaxSmall)
  ) {
    throw new Error("Line current limits must be valid numbers.");
  }
  if (unloadedMinLarge > unloadedMaxLarge || unloadedMinSmall > unloadedMaxSmall) {
    throw new Error("Each line’s minimum unloaded current must be less than or equal to its maximum.");
  }
  return {
    unloadedMinLarge,
    unloadedMaxLarge,
    unloadedMinSmall,
    unloadedMaxSmall,
  };
}

function buildDragPatch(
  dragGrayMinLargeDraft: string,
  dragPassMinLargeDraft: string,
  dragPassMaxLargeDraft: string,
  dragGrayMaxLargeDraft: string,
  dragGrayMinSmallDraft: string,
  dragPassMinSmallDraft: string,
  dragPassMaxSmallDraft: string,
  dragGrayMaxSmallDraft: string,
): Pick<
  ClampQcHubSettingsPatchRequest,
  | "dragGrayMinLarge"
  | "dragPassMinLarge"
  | "dragPassMaxLarge"
  | "dragGrayMaxLarge"
  | "dragGrayMinSmall"
  | "dragPassMinSmall"
  | "dragPassMaxSmall"
  | "dragGrayMaxSmall"
> {
  const dragGrayMinLarge = Number.parseFloat(dragGrayMinLargeDraft.trim());
  const dragPassMinLarge = Number.parseFloat(dragPassMinLargeDraft.trim());
  const dragPassMaxLarge = Number.parseFloat(dragPassMaxLargeDraft.trim());
  const dragGrayMaxLarge = Number.parseFloat(dragGrayMaxLargeDraft.trim());
  const dragGrayMinSmall = Number.parseFloat(dragGrayMinSmallDraft.trim());
  const dragPassMinSmall = Number.parseFloat(dragPassMinSmallDraft.trim());
  const dragPassMaxSmall = Number.parseFloat(dragPassMaxSmallDraft.trim());
  const dragGrayMaxSmall = Number.parseFloat(dragGrayMaxSmallDraft.trim());
  if (
    !Number.isFinite(dragGrayMinLarge) ||
    !Number.isFinite(dragPassMinLarge) ||
    !Number.isFinite(dragPassMaxLarge) ||
    !Number.isFinite(dragGrayMaxLarge) ||
    !Number.isFinite(dragGrayMinSmall) ||
    !Number.isFinite(dragPassMinSmall) ||
    !Number.isFinite(dragPassMaxSmall) ||
    !Number.isFinite(dragGrayMaxSmall)
  ) {
    throw new Error("Drag ladder values must be valid numbers.");
  }
  const orderLargeOk =
    dragGrayMinLarge <= dragPassMinLarge &&
    dragPassMinLarge <= dragPassMaxLarge &&
    dragPassMaxLarge <= dragGrayMaxLarge;
  const orderSmallOk =
    dragGrayMinSmall <= dragPassMinSmall &&
    dragPassMinSmall <= dragPassMaxSmall &&
    dragPassMaxSmall <= dragGrayMaxSmall;
  if (!orderLargeOk || !orderSmallOk) {
    throw new Error(
      "Each line needs gray minimum ≤ pass minimum ≤ pass maximum ≤ gray maximum (inclusive drag windows).",
    );
  }
  return {
    dragGrayMinLarge,
    dragPassMinLarge,
    dragPassMaxLarge,
    dragGrayMaxLarge,
    dragGrayMinSmall,
    dragPassMinSmall,
    dragPassMaxSmall,
    dragGrayMaxSmall,
  };
}

function buildFullClampQcPatch(args: {
  largeDraft: string;
  smallDraft: string;
  inductanceTestingPercentDraft: string;
  indMinLargeDraft: string;
  indMaxLargeDraft: string;
  indMinSmallDraft: string;
  indMaxSmallDraft: string;
  unloadedMinLargeDraft: string;
  unloadedMaxLargeDraft: string;
  unloadedMinSmallDraft: string;
  unloadedMaxSmallDraft: string;
  dragGrayMinLargeDraft: string;
  dragPassMinLargeDraft: string;
  dragPassMaxLargeDraft: string;
  dragGrayMaxLargeDraft: string;
  dragGrayMinSmallDraft: string;
  dragPassMinSmallDraft: string;
  dragPassMaxSmallDraft: string;
  dragGrayMaxSmallDraft: string;
}): ClampQcHubSettingsPatchRequest {
  return {
    ...buildCartonsPatch(args.largeDraft, args.smallDraft),
    ...buildInductanceRatePatch(args.inductanceTestingPercentDraft),
    ...buildInductanceBandPatch(
      args.indMinLargeDraft,
      args.indMaxLargeDraft,
      args.indMinSmallDraft,
      args.indMaxSmallDraft,
    ),
    ...buildLineCurrentPatch(
      args.unloadedMinLargeDraft,
      args.unloadedMaxLargeDraft,
      args.unloadedMinSmallDraft,
      args.unloadedMaxSmallDraft,
    ),
    ...buildDragPatch(
      args.dragGrayMinLargeDraft,
      args.dragPassMinLargeDraft,
      args.dragPassMaxLargeDraft,
      args.dragGrayMaxLargeDraft,
      args.dragGrayMinSmallDraft,
      args.dragPassMinSmallDraft,
      args.dragPassMaxSmallDraft,
      args.dragGrayMaxSmallDraft,
    ),
  };
}

type ClampQcSettingsSaveMutation = UseMutationResult<ClampQcHubSettingsResponse, Error, SectionSaveKind>;

function ClampQcSectionSaveFooter(props: {
  section: Exclude<SectionSaveKind, "all">;
  buttonLabel: string;
  saveMutation: ClampQcSettingsSaveMutation;
  formFieldsDisabled: boolean;
  saveHoverActive: boolean;
  saveHoverMessage: string;
  isDirty: boolean;
}) {
  const { section, buttonLabel, saveMutation, formFieldsDisabled, saveHoverActive, saveHoverMessage, isDirty } =
    props;
  const showError = saveMutation.isError && saveMutation.variables === section;
  const showSuccess = saveMutation.isSuccess && saveMutation.variables === section;
  return (
    <div className="space-y-2">
      <div className="min-h-5 text-sm leading-5">
        {showError ? (
          <p className="text-rose-600">
            {saveMutation.error instanceof Error ? saveMutation.error.message : "Save failed."}
          </p>
        ) : showSuccess ? (
          <p className="text-emerald-800" role="status">
            Saved.
          </p>
        ) : (
          <p className="invisible select-none" aria-hidden="true">
            {"\u00a0"}
          </p>
        )}
      </div>
      <div className="flex justify-end">
        <HubSaveButton
          type="button"
          dirty={isDirty}
          disabled={formFieldsDisabled}
          blockedHint={saveHoverActive ? saveHoverMessage : undefined}
          isLoading={saveMutation.isPending && saveMutation.variables === section}
          onClick={() => void saveMutation.mutate(section)}
        >
          {buttonLabel}
        </HubSaveButton>
      </div>
    </div>
  );
}

export function ClampQcSettingsPage() {
  const queryClient = useQueryClient();
  const hubClampQcSettingsAllowed = useHubModuleAdmin("clampqc");

  const settingsQuery = useQuery({
    queryKey: CLAMPQC_HUB_SETTINGS_QUERY_KEY,
    queryFn: () => fetchClampQcHubSettings(),
    staleTime: 15_000,
  });

  const hubClampSettingsMockup = useQuery({
    queryKey: ["hub-mockup", "clampqc", "settings"],
    queryFn: () => fetchHubMockupEffective("clampqc", "settings"),
    staleTime: 60_000,
  });

  const settingsPageMockupBlocksWrites = hubClampSettingsMockup.data?.writesBlocked === true;
  const formFieldsDisabled =
    !hubClampQcSettingsAllowed || settingsQuery.isLoading || settingsPageMockupBlocksWrites;
  const saveBlockedByRole = !hubClampQcSettingsAllowed;
  const saveHoverActive = saveBlockedByRole || settingsPageMockupBlocksWrites;
  const saveHoverMessage =
    saveBlockedByRole && settingsPageMockupBlocksWrites
      ? "Saves are disabled: module administrator access is required for Clamp quality control hub settings, and hub mock-up blocks writes for this page."
      : saveBlockedByRole
        ? "Module administrator access is required to change Clamp quality control hub settings."
        : "Hub mock-up is turned on for Clamp QC Settings; saves are blocked until mock-up is cleared for this page.";

  const [largeDraft, setLargeDraft] = useState("");
  const [smallDraft, setSmallDraft] = useState("");
  const [indMinLargeDraft, setIndMinLargeDraft] = useState("");
  const [indMaxLargeDraft, setIndMaxLargeDraft] = useState("");
  const [indMinSmallDraft, setIndMinSmallDraft] = useState("");
  const [indMaxSmallDraft, setIndMaxSmallDraft] = useState("");
  const [unloadedMinLargeDraft, setUnloadedMinLargeDraft] = useState("");
  const [unloadedMaxLargeDraft, setUnloadedMaxLargeDraft] = useState("");
  const [unloadedMinSmallDraft, setUnloadedMinSmallDraft] = useState("");
  const [unloadedMaxSmallDraft, setUnloadedMaxSmallDraft] = useState("");
  const [dragGrayMinLargeDraft, setDragGrayMinLargeDraft] = useState("");
  const [dragPassMinLargeDraft, setDragPassMinLargeDraft] = useState("");
  const [dragPassMaxLargeDraft, setDragPassMaxLargeDraft] = useState("");
  const [dragGrayMaxLargeDraft, setDragGrayMaxLargeDraft] = useState("");
  const [dragGrayMinSmallDraft, setDragGrayMinSmallDraft] = useState("");
  const [dragPassMinSmallDraft, setDragPassMinSmallDraft] = useState("");
  const [dragPassMaxSmallDraft, setDragPassMaxSmallDraft] = useState("");
  const [dragGrayMaxSmallDraft, setDragGrayMaxSmallDraft] = useState("");
  const [inductanceTestingPercentDraft, setInductanceTestingPercentDraft] = useState("");

  useEffect(() => {
    if (!settingsQuery.data) {
      return;
    }
    const data = settingsQuery.data;
    setLargeDraft(String(data.unitsPerCartonLarge));
    setSmallDraft(String(data.unitsPerCartonSmall));
    setIndMinLargeDraft(String(data.inductanceMinLarge));
    setIndMaxLargeDraft(String(data.inductanceMaxLarge));
    setIndMinSmallDraft(String(data.inductanceMinSmall));
    setIndMaxSmallDraft(String(data.inductanceMaxSmall));
    setUnloadedMinLargeDraft(String(data.unloadedMinLarge));
    setUnloadedMaxLargeDraft(String(data.unloadedMaxLarge));
    setUnloadedMinSmallDraft(String(data.unloadedMinSmall));
    setUnloadedMaxSmallDraft(String(data.unloadedMaxSmall));
    setDragGrayMinLargeDraft(String(data.dragGrayMinLarge));
    setDragPassMinLargeDraft(String(data.dragPassMinLarge));
    setDragPassMaxLargeDraft(String(data.dragPassMaxLarge));
    setDragGrayMaxLargeDraft(String(data.dragGrayMaxLarge));
    setDragGrayMinSmallDraft(String(data.dragGrayMinSmall));
    setDragPassMinSmallDraft(String(data.dragPassMinSmall));
    setDragPassMaxSmallDraft(String(data.dragPassMaxSmall));
    setDragGrayMaxSmallDraft(String(data.dragGrayMaxSmall));
    const percent = data.inductanceTestingPercentPerWorkOrder;
    setInductanceTestingPercentDraft(
      typeof percent === "number" && Number.isFinite(percent) ? String(percent) : "",
    );
  }, [settingsQuery.data]);

  const saveMutation = useMutation<ClampQcHubSettingsResponse, Error, SectionSaveKind>({
    mutationFn: async (kind) => {
      switch (kind) {
        case "cartons":
          return patchClampQcHubSettings(buildCartonsPatch(largeDraft, smallDraft));
        case "inductanceRate":
          return patchClampQcHubSettings(buildInductanceRatePatch(inductanceTestingPercentDraft));
        case "inductanceBand":
          return patchClampQcHubSettings(
            buildInductanceBandPatch(indMinLargeDraft, indMaxLargeDraft, indMinSmallDraft, indMaxSmallDraft),
          );
        case "lineCurrent":
          return patchClampQcHubSettings(
            buildLineCurrentPatch(
              unloadedMinLargeDraft,
              unloadedMaxLargeDraft,
              unloadedMinSmallDraft,
              unloadedMaxSmallDraft,
            ),
          );
        case "drag":
          return patchClampQcHubSettings(
            buildDragPatch(
              dragGrayMinLargeDraft,
              dragPassMinLargeDraft,
              dragPassMaxLargeDraft,
              dragGrayMaxLargeDraft,
              dragGrayMinSmallDraft,
              dragPassMinSmallDraft,
              dragPassMaxSmallDraft,
              dragGrayMaxSmallDraft,
            ),
          );
        case "all":
          return patchClampQcHubSettings(
            buildFullClampQcPatch({
              largeDraft,
              smallDraft,
              inductanceTestingPercentDraft,
              indMinLargeDraft,
              indMaxLargeDraft,
              indMinSmallDraft,
              indMaxSmallDraft,
              unloadedMinLargeDraft,
              unloadedMaxLargeDraft,
              unloadedMinSmallDraft,
              unloadedMaxSmallDraft,
              dragGrayMinLargeDraft,
              dragPassMinLargeDraft,
              dragPassMaxLargeDraft,
              dragGrayMaxLargeDraft,
              dragGrayMinSmallDraft,
              dragPassMinSmallDraft,
              dragPassMaxSmallDraft,
              dragGrayMaxSmallDraft,
            }),
          );
      }
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(CLAMPQC_HUB_SETTINGS_QUERY_KEY, saved);
      void queryClient.invalidateQueries({ queryKey: CLAMPQC_HUB_SETTINGS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: CLAMPQC_PHYSICAL_FAILURE_REASONS_QUERY_KEY });
    },
  });

  const persisted = settingsQuery.data;
  const cartonsDirty =
    persisted != null &&
    (largeDraft !== String(persisted.unitsPerCartonLarge) || smallDraft !== String(persisted.unitsPerCartonSmall));
  const persistedInductancePercent =
    persisted != null &&
    typeof persisted.inductanceTestingPercentPerWorkOrder === "number" &&
    Number.isFinite(persisted.inductanceTestingPercentPerWorkOrder)
      ? String(persisted.inductanceTestingPercentPerWorkOrder)
      : "";
  const inductanceRateDirty =
    persisted != null && inductanceTestingPercentDraft !== persistedInductancePercent;
  const inductanceBandDirty =
    persisted != null &&
    (indMinLargeDraft !== String(persisted.inductanceMinLarge) ||
      indMaxLargeDraft !== String(persisted.inductanceMaxLarge) ||
      indMinSmallDraft !== String(persisted.inductanceMinSmall) ||
      indMaxSmallDraft !== String(persisted.inductanceMaxSmall));
  const lineCurrentDirty =
    persisted != null &&
    (unloadedMinLargeDraft !== String(persisted.unloadedMinLarge) ||
      unloadedMaxLargeDraft !== String(persisted.unloadedMaxLarge) ||
      unloadedMinSmallDraft !== String(persisted.unloadedMinSmall) ||
      unloadedMaxSmallDraft !== String(persisted.unloadedMaxSmall));
  const dragDirty =
    persisted != null &&
    (dragGrayMinLargeDraft !== String(persisted.dragGrayMinLarge) ||
      dragPassMinLargeDraft !== String(persisted.dragPassMinLarge) ||
      dragPassMaxLargeDraft !== String(persisted.dragPassMaxLarge) ||
      dragGrayMaxLargeDraft !== String(persisted.dragGrayMaxLarge) ||
      dragGrayMinSmallDraft !== String(persisted.dragGrayMinSmall) ||
      dragPassMinSmallDraft !== String(persisted.dragPassMinSmall) ||
      dragPassMaxSmallDraft !== String(persisted.dragPassMaxSmall) ||
      dragGrayMaxSmallDraft !== String(persisted.dragGrayMaxSmall));
  const anyDirty = cartonsDirty || inductanceRateDirty || inductanceBandDirty || lineCurrentDirty || dragDirty;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-2">
        <PageTitleWithHelp hoverHint={CLAMP_QC_SETTINGS_PAGE_HELP_TEXT}>
          Clamp quality control · Settings
        </PageTitleWithHelp>
      </header>

      <HubSettingsSection
        title="Clamps per shipping carton"
        hoverHint={CLAMP_QC_CARTONS_SECTION_HELP_TEXT}
        footer={
          <ClampQcSectionSaveFooter
            section="cartons"
            buttonLabel="Save clamps per carton"
            saveMutation={saveMutation}
            formFieldsDisabled={formFieldsDisabled}
            saveHoverActive={saveHoverActive}
            saveHoverMessage={saveHoverMessage}
            isDirty={cartonsDirty}
          />
        }
      >
        {settingsQuery.isError ? (
          <p className="text-sm text-rose-600">
            {settingsQuery.error instanceof ApiError
              ? settingsQuery.error.message
              : "Could not load settings. Check that you are signed in and have Clamp QC access."}
          </p>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="clamp-qc-units-large">Large clamp line</Label>
              <Input
                id="clamp-qc-units-large"
                type="number"
                min={1}
                max={999}
                className="mt-1 tabular-nums"
                value={largeDraft}
                onChange={(event) => setLargeDraft(event.target.value)}
                disabled={formFieldsDisabled}
                inputMode="numeric"
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="clamp-qc-units-small">Small clamp line</Label>
              <Input
                id="clamp-qc-units-small"
                type="number"
                min={1}
                max={999}
                className="mt-1 tabular-nums"
                value={smallDraft}
                onChange={(event) => setSmallDraft(event.target.value)}
                disabled={formFieldsDisabled}
                inputMode="numeric"
                autoComplete="off"
              />
            </div>
          </div>
      </HubSettingsSection>

      <HubSettingsSection
        title="Inductance testing rate per work order"
        hoverHint={CLAMP_QC_INDUCTANCE_RATE_SECTION_HELP_TEXT}
        footer={
          <ClampQcSectionSaveFooter
            section="inductanceRate"
            buttonLabel="Save inductance sampling rate"
            saveMutation={saveMutation}
            formFieldsDisabled={formFieldsDisabled}
            saveHoverActive={saveHoverActive}
            saveHoverMessage={saveHoverMessage}
            isDirty={inductanceRateDirty}
          />
        }
      >
        <div>
          <Label htmlFor="clamp-qc-inductance-testing-percent">Percent of planned units that require inductance</Label>
          <Input
            id="clamp-qc-inductance-testing-percent"
            type="number"
            min={0}
            max={100}
            step={0.1}
            className="mt-1 max-w-xs tabular-nums"
            value={inductanceTestingPercentDraft}
            onChange={(event) => setInductanceTestingPercentDraft(event.target.value)}
            disabled={formFieldsDisabled}
            inputMode="decimal"
            autoComplete="off"
          />
          <p className="mt-3 max-w-2xl rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
            After you lower this percent, only newly reserved serials pick up the new rate. If every unit on one work
            order still demands inductance, that build order likely has older rows in{" "}
            <span className="font-mono text-xs">workflow.clamp_qc_inspection_slot</span> from before the change, or the
            stored hub JSON is missing this field (the hub returns 422 instead of guessing a rate).
          </p>
        </div>
      </HubSettingsSection>

      <HubSettingsSection
        title="Inductance acceptance band"
        hoverHint="When an operator enters a measured inductance on the inspection modal, the unit fails automatically if the value is strictly outside this inclusive band. Different limits apply to large (90-003) versus small (90-002) job SKUs. Leave the field blank on the floor if you are not recording inductance yet."
        footer={
          <ClampQcSectionSaveFooter
            section="inductanceBand"
            buttonLabel="Save inductance bands"
            saveMutation={saveMutation}
            formFieldsDisabled={formFieldsDisabled}
            saveHoverActive={saveHoverActive}
            saveHoverMessage={saveHoverMessage}
            isDirty={inductanceBandDirty}
          />
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-sm font-semibold text-slate-900">
                Large line <span className="font-mono text-xs font-normal text-slate-600">90-003…</span>
              </p>
              <div>
                <Label htmlFor="clamp-qc-ind-min-large">Minimum</Label>
                <Input
                  id="clamp-qc-ind-min-large"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={indMinLargeDraft}
                  onChange={(event) => setIndMinLargeDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="clamp-qc-ind-max-large">Maximum</Label>
                <Input
                  id="clamp-qc-ind-max-large"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={indMaxLargeDraft}
                  onChange={(event) => setIndMaxLargeDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-sm font-semibold text-slate-900">
                Small line <span className="font-mono text-xs font-normal text-slate-600">90-002…</span>
              </p>
              <div>
                <Label htmlFor="clamp-qc-ind-min-small">Minimum</Label>
                <Input
                  id="clamp-qc-ind-min-small"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={indMinSmallDraft}
                  onChange={(event) => setIndMinSmallDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="clamp-qc-ind-max-small">Maximum</Label>
                <Input
                  id="clamp-qc-ind-max-small"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={indMaxSmallDraft}
                  onChange={(event) => setIndMaxSmallDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>
      </HubSettingsSection>

      <HubSettingsSection
        title="Line current limits"
        hoverHint="Unloaded line is the baseline, checked before the drag test. Prefix 90-003 and prefix 90-002 each have their own inclusive minimum and maximum. Outside the band, Record stays disabled. That does not fail or scrap the unit. The loaded line reading is not a pass or fail by itself."
        footer={
          <ClampQcSectionSaveFooter
            section="lineCurrent"
            buttonLabel="Save line current limits"
            saveMutation={saveMutation}
            formFieldsDisabled={formFieldsDisabled}
            saveHoverActive={saveHoverActive}
            saveHoverMessage={saveHoverMessage}
            isDirty={lineCurrentDirty}
          />
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-sm font-semibold text-slate-900">
                Large line <span className="font-mono text-xs font-normal text-slate-600">90-003…</span>
              </p>
              <div>
                <Label htmlFor="clamp-qc-unloaded-min-large">Unloaded minimum</Label>
                <Input
                  id="clamp-qc-unloaded-min-large"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={unloadedMinLargeDraft}
                  onChange={(event) => setUnloadedMinLargeDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="clamp-qc-unloaded-max-large">Unloaded maximum</Label>
                <Input
                  id="clamp-qc-unloaded-max-large"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={unloadedMaxLargeDraft}
                  onChange={(event) => setUnloadedMaxLargeDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-sm font-semibold text-slate-900">
                Small line <span className="font-mono text-xs font-normal text-slate-600">90-002…</span>
              </p>
              <div>
                <Label htmlFor="clamp-qc-unloaded-min-small">Unloaded minimum</Label>
                <Input
                  id="clamp-qc-unloaded-min-small"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={unloadedMinSmallDraft}
                  onChange={(event) => setUnloadedMinSmallDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="clamp-qc-unloaded-max-small">Unloaded maximum</Label>
                <Input
                  id="clamp-qc-unloaded-max-small"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={unloadedMaxSmallDraft}
                  onChange={(event) => setUnloadedMaxSmallDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>
      </HubSettingsSection>

      <HubSettingsSection
        title="Drag windows (line harvest)"
        hoverHint="Drag is the delta: unloaded line minus loaded line before tester. Prefix 90-003 and prefix 90-002 each have their own window. Inside the pass band, line harvest passes automatically. Inside the gray window but outside the pass band, the operator hooks to a tester, enters loaded line with tester, and records Line harvest Good or Failed. Outside the gray window entirely, the unit fails automatically. The loaded line reading is not a pass or fail by itself. Unloaded line is remembered between clamps on the same job."
        footer={
          <ClampQcSectionSaveFooter
            section="drag"
            buttonLabel="Save drag windows"
            saveMutation={saveMutation}
            formFieldsDisabled={formFieldsDisabled}
            saveHoverActive={saveHoverActive}
            saveHoverMessage={saveHoverMessage}
            isDirty={dragDirty}
          />
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-sm font-semibold text-slate-900">
                Large line <span className="font-mono text-xs font-normal text-slate-600">90-003…</span>
              </p>
              <div>
                <Label htmlFor="clamp-qc-drag-gray-min-lg">Gray minimum</Label>
                <Input
                  id="clamp-qc-drag-gray-min-lg"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={dragGrayMinLargeDraft}
                  onChange={(event) => setDragGrayMinLargeDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="clamp-qc-drag-pass-min-lg">Pass minimum</Label>
                <Input
                  id="clamp-qc-drag-pass-min-lg"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={dragPassMinLargeDraft}
                  onChange={(event) => setDragPassMinLargeDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="clamp-qc-drag-pass-max-lg">Pass maximum</Label>
                <Input
                  id="clamp-qc-drag-pass-max-lg"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={dragPassMaxLargeDraft}
                  onChange={(event) => setDragPassMaxLargeDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="clamp-qc-drag-gray-max-lg">Gray maximum</Label>
                <Input
                  id="clamp-qc-drag-gray-max-lg"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={dragGrayMaxLargeDraft}
                  onChange={(event) => setDragGrayMaxLargeDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-sm font-semibold text-slate-900">
                Small line <span className="font-mono text-xs font-normal text-slate-600">90-002…</span>
              </p>
              <div>
                <Label htmlFor="clamp-qc-drag-gray-min-sm">Gray minimum</Label>
                <Input
                  id="clamp-qc-drag-gray-min-sm"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={dragGrayMinSmallDraft}
                  onChange={(event) => setDragGrayMinSmallDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="clamp-qc-drag-pass-min-sm">Pass minimum</Label>
                <Input
                  id="clamp-qc-drag-pass-min-sm"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={dragPassMinSmallDraft}
                  onChange={(event) => setDragPassMinSmallDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="clamp-qc-drag-pass-max-sm">Pass maximum</Label>
                <Input
                  id="clamp-qc-drag-pass-max-sm"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={dragPassMaxSmallDraft}
                  onChange={(event) => setDragPassMaxSmallDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="clamp-qc-drag-gray-max-sm">Gray maximum</Label>
                <Input
                  id="clamp-qc-drag-gray-max-sm"
                  type="text"
                  inputMode="decimal"
                  className="mt-1 tabular-nums"
                  value={dragGrayMaxSmallDraft}
                  onChange={(event) => setDragGrayMaxSmallDraft(event.target.value)}
                  disabled={formFieldsDisabled}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>
      </HubSettingsSection>

      <ClampQcPhysicalFailReasonsSettingsSection
        canEdit={hubClampQcSettingsAllowed}
        formFieldsDisabled={formFieldsDisabled}
      />

      <NotificationTopicOwnersSettingsSection
        topic="clamp_qc_ready_for_credit"
        title="QC credit ready distribution list"
        hoverHint="Hub users in this group receive email and in-app notifications when a clamp QC work order finishes receiving passed units to stock so QC credits can be claimed."
        canEdit={hubClampQcSettingsAllowed}
        userOptionsQueryKey={NOTIFICATION_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchNotificationStaffUsers}
        readOnlyHoverHint="Clamp QC module administrators can edit this distribution list."
      />

      <HubSettingsFooter className="sticky bottom-0 z-10 -mx-px rounded-lg border border-slate-200 bg-slate-50/95 px-4 py-4 shadow-[0_-6px_16px_rgba(15,23,42,0.06)] backdrop-blur sm:px-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <p className="text-xs text-slate-500">
            {settingsQuery.data?.updatedAt != null
              ? `Last saved ${formatTimestamp(settingsQuery.data.updatedAt)}.`
              : settingsQuery.isSuccess
                ? "Using defaults until first save."
                : "Loading…"}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <HubSaveButton
              type="button"
              dirty={anyDirty}
              disabled={formFieldsDisabled}
              blockedHint={saveHoverActive ? saveHoverMessage : undefined}
              isLoading={saveMutation.isPending && saveMutation.variables === "all"}
              onClick={() => void saveMutation.mutate("all")}
            >
              Save all settings
            </HubSaveButton>
            {saveMutation.isError && saveMutation.variables === "all" ? (
              <p className="text-sm text-rose-600">
                {saveMutation.error instanceof Error ? saveMutation.error.message : "Save failed."}
              </p>
            ) : null}
            {saveMutation.isSuccess && saveMutation.variables === "all" ? (
              <p className="text-sm text-emerald-800" role="status">
                Saved. Open job pages pick this up automatically.
              </p>
            ) : null}
          </div>
        </div>
      </HubSettingsFooter>
    </div>
  );
}
